import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { APP_SETTINGS } from '@services';
import { SubscriptionLike as ISubscription } from 'rxjs';
import { HandleError } from '../../core/models/handle-error';
import { AnalyticsService } from '../../core/services/analytics/analytics.service';
import { SpinnerService } from '../../core/services/spinner/spinner.service';
import { UtilityService } from '../../core/services/utility/utility.service';
import { ModelMapService } from './model-map.service';
import { NgIf, NgFor, NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalMapPipe } from '../../shared/pipes/modal-map.pipe';

interface ModelMap {
  'id': number;
  'year': string;
  'agCode': string;
  'modelName': string;
  'seriesName': string;
  'engineName': string;
  'bodyName': string;
  'agCodeName': string;
}

type FilterType = 'model' | 'series' | 'engine' | 'year';

@Component({
    selector: 'app-model-map',
    templateUrl: './model-map.component.html',
    styleUrls: ['./model-map.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        FormsModule,
        NgFor,
        NgStyle,
        ModalMapPipe,
    ],
})

export class ModelMapComponent implements OnInit, OnDestroy {
  errorMessage: string;

  screenWidth: number;

  APP_SETTINGS: any;

  public inputModel;
  public inputSeries;
  public inputEngine;
  public inputYear;

  modelFilter: string = '';
  seriesFilter: string = '';
  engineFilter: string = '';
  yearFilter: string = '';

  public modelList = [];
  public seriesList = [];
  public engineList = [];
  public yearList = [];
  public modelMapObjAll: ModelMap[];
  public modelMapObjDisplay: ModelMap[];
  public searchFilter = { 'modelName': '', 'seriesName': '', 'engineName': '', 'year': '' };
  public mapFilterKeyToValue = { 'modelName': 'Model', 'seriesName': 'Series', 'engineName': 'Engine', 'year': 'Year' };
  public objectKeys = Object.keys(this.searchFilter);
  public serviceError = 'Loading data. Please wait...';

  private subscriptions: ISubscription[];
  public modelMapAll: any[];

  public filteredModelList: string[] = [];
  public filteredSeriesList: string[] = [];
  public filteredEngineList: string[] = [];
  public filteredYearList: string[] = [];

  public showModelDropdown = false;
  public showSeriesDropdown = false;
  public showEngineDropdown = false;
  public showYearDropdown = false;

  @ViewChild('resultsContent')
  private resultsContentDiv: ElementRef;

  @ViewChild('engineInput')
  engineInput: ElementRef;


  constructor(
    private utilityService: UtilityService,
    private spinnerService: SpinnerService,
    private analyticsService: AnalyticsService,
    private modelMapService: ModelMapService
  ) {
    this.screenWidth = this.utilityService.getScreenWidth();
  }

  ngOnInit() {
    this.APP_SETTINGS = APP_SETTINGS;
    this.subscriptions = [];
    this.modelMapAll = [];
    
    this.filteredModelList = [];
    this.filteredSeriesList = [];
    this.filteredEngineList = [];
    this.filteredYearList = [];
    
    this.modelMapService.getAllModelMapData().subscribe(
      data => {
        this.spinnerService.show('bwmSpinner');
        if (data && Array.isArray(data)) {
          this.modelMapObjAll = <ModelMap[]>data;
          this.modelMapObjDisplay = this.modelMapObjAll;
          this.filterMapList(this.modelMapObjDisplay);
        } else {
          console.error('Invalid data received from backend:', data);
          this.modelMapObjAll = [];
          this.modelMapObjDisplay = [];
          this.serviceError = 'Error loading model map data. Please try again later.';
        }
        
        this.spinnerService.hide('bmwSpinner');
      },
      error => {
        this.spinnerService.hide('bmwSpinner');
        this.serviceError = HandleError.handleAPIError(error);
        this.modelMapObjAll = [];
        this.modelMapObjDisplay = [];
      }
    );


    // ORIGINAL MODEL MAP METHOD
    // this.spinnerService.show('bmwSpinner');
    // this.subscriptions.push(this.modelMapService.getAllModels().subscribe(models => {
    //   this.modelList = models;
    //   this.subscriptions.push(this.modelMapService.getAllSeries().subscribe(series => {
    //     this.seriesList = series;
    //     this.subscriptions.push(this.modelMapService.getAllEngines().subscribe(engines => {
    //       this.engineList = engines;
    //       this.subscriptions.push(this.modelMapService.getAllYears().subscribe(years => {
    //         this.yearList = years;
    //         this.spinnerService.hide('bmwSpinner');
    //       },
    //         error => {
    //           this.spinnerService.hide('bmwSpinner');
    //           this.serviceError = HandleError.handleAPIError(error);
    //         }
    //       ));
    //     },
    //       error => {
    //         this.spinnerService.hide('bmwSpinner');
    //         this.serviceError = HandleError.handleAPIError(error);
    //       }
    //     ));
    //   },
    //     error => {
    //       this.spinnerService.hide('bmwSpinner');
    //       this.serviceError = HandleError.handleAPIError(error);
    //     }
    //   ));
    // },
    //   error => {
    //     this.spinnerService.hide('bmwSpinner');
    //     this.serviceError = HandleError.handleAPIError(error);
    //   }
    // ));



    // this.subscriptions.push(this.modelMapService.getAllModelMapData().subscribe(data => {
    //   this.modelMapAll = data;
    // },
    //   error => this.serviceError = HandleError.handleAPIError(error)
    // ));
  }

  filterOptions(type: FilterType) {
    if (!this.modelMapObjAll || !Array.isArray(this.modelMapObjAll)) {
      return;
    }
    
    const inputValue = this.getInputValueByType(type);
    
    if (!inputValue || inputValue.trim() === '') {
      this.clearFilteredList(type);
      return;
    }
    
    const availableData = this.getFilteredDataByType(type);
    const uniqueValues = this.extractUniqueValues(availableData, type);
    this.updateFilteredListAndDropdown(type, uniqueValues);
  }

  onFieldSelected(type: FilterType) {
    const inputValue = this.getInputValueByType(type);
    if (inputValue) {
      this.trackAnalytics(type, inputValue);
      this.updateFiltersAndResults();
    }
  }

  private getInputValueByType(type: FilterType): string {
    const inputMap = {
      model: this.inputModel,
      series: this.inputSeries,
      engine: this.inputEngine,
      year: this.inputYear
    };
    return inputMap[type];
  }

  private trackAnalytics(type: FilterType, value: string) {
    const analyticsMap = {
      model: () => this.analyticsService.trackModelMapModelValue(value),
      series: () => this.analyticsService.trackModelMapSeriesValue(value),
      engine: () => this.analyticsService.trackModelMapEngineValue(value),
      year: () => this.analyticsService.trackModelMapYearValue(value)
    };
    analyticsMap[type]();
  }

  private getFilteredDataByType(type: FilterType): ModelMap[] {
    let data = [...this.modelMapObjAll];
    
    const filters = {
      model: ['inputSeries', 'inputEngine', 'inputYear'],
      series: ['inputModel', 'inputEngine', 'inputYear'],
      engine: ['inputModel', 'inputSeries', 'inputYear'],
      year: ['inputModel', 'inputSeries', 'inputEngine']
    };
    
    const filterMap = {
      inputModel: 'modelName',
      inputSeries: 'seriesName',
      inputEngine: 'engineName',
      inputYear: 'year'
    };
    
    filters[type].forEach(inputProp => {
      const inputValue = this[inputProp];
      if (inputValue) {
        const dataProp = filterMap[inputProp];
        data = data.filter(v => v?.[dataProp] === inputValue);
      }
    });
    
    return data;
  }

  private extractUniqueValues(data: ModelMap[], type: FilterType): string[] {
    const propertyMap = {
      model: 'modelName',
      series: 'seriesName',
      engine: 'engineName',
      year: 'year'
    };
    
    const property = propertyMap[type];
    const uniqueValues = [...new Set(data.map(item => item?.[property]).filter(value => value != null))];
    return this.sortValues(uniqueValues, type);
  }

  private sortValues(values: string[], type: FilterType): string[] {
    return values.sort((a, b) => {
      // For year, parse as numbers
      if (type === 'year') {
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
      }
      // Natural sort with numeric support for series like X1, X2, X10
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  private updateFilteredListAndDropdown(type: FilterType, availableValues: string[]) {
    const inputMap = {
      model: 'inputModel',
      series: 'inputSeries',
      engine: 'inputEngine',
      year: 'inputYear'
    };
    
    const listMap = {
      model: 'filteredModelList',
      series: 'filteredSeriesList',
      engine: 'filteredEngineList',
      year: 'filteredYearList'
    };
    
    const dropdownMap = {
      model: 'showModelDropdown',
      series: 'showSeriesDropdown',
      engine: 'showEngineDropdown',
      year: 'showYearDropdown'
    };
    
    const inputValue = this[inputMap[type]];
    const filteredList = inputValue 
      ? availableValues.filter(v => v?.toLowerCase().includes(inputValue.toLowerCase()))
      : availableValues;
    
    this[listMap[type]] = filteredList;
    this[dropdownMap[type]] = inputValue ? filteredList.length > 0 : false;
  }

  private clearFilteredList(type: FilterType) {
    const listMap = {
      model: 'filteredModelList',
      series: 'filteredSeriesList',
      engine: 'filteredEngineList',
      year: 'filteredYearList'
    };
    
    const dropdownMap = {
      model: 'showModelDropdown',
      series: 'showSeriesDropdown',
      engine: 'showEngineDropdown',
      year: 'showYearDropdown'
    };
    
    this[listMap[type]] = [];
    this[dropdownMap[type]] = false;
  }

  selectOption(type: FilterType, value: string) {
    switch (type) {
      case 'model':
        this.inputModel = value;
        this.analyticsService.trackModelMapModelValue(value);
        this.showModelDropdown = false;
        break;
      case 'series':
        this.inputSeries = value;
        this.analyticsService.trackModelMapSeriesValue(value);
        this.showSeriesDropdown = false;
        break;
      case 'engine':
        this.inputEngine = value;
        this.analyticsService.trackModelMapEngineValue(value);
        this.showEngineDropdown = false;
        break;
      case 'year':
        this.inputYear = value;
        this.analyticsService.trackModelMapYearValue(value);
        this.showYearDropdown = false;
        break;
    }
    this.updateFiltersAndResults();
  }

  updateFiltersAndResults() {
    this.spinnerService.show('bmwSpinner');

    if (!this.modelMapObjAll || !Array.isArray(this.modelMapObjAll)) {
      this.spinnerService.hide('bmwSpinner');
      return;
    }

    let filteredData = [...this.modelMapObjAll];

    if (this.inputModel) {
      filteredData = filteredData.filter(v => v?.modelName === this.inputModel);
    }
    if (this.inputSeries) {
      filteredData = filteredData.filter(v => v?.seriesName === this.inputSeries);
    }
    if (this.inputEngine) {
      filteredData = filteredData.filter(v => v?.engineName === this.inputEngine);
    }
    if (this.inputYear) {
      filteredData = filteredData.filter(v => v?.year === this.inputYear);
    }

    this.modelMapAll = filteredData;
    this.errorMessage = this.modelMapAll.length === 0 ? 'No Vehicle Information Found' : '';
    
    this.populateAllOptionLists(filteredData);
    
    this.spinnerService.hide('bmwSpinner');
  }

  populateAllOptionLists(data: ModelMap[]) {
    if (!data || !Array.isArray(data)) {
      this.modelList = [];
      this.seriesList = [];
      this.engineList = [];
      this.yearList = [];
      return;
    }
    
    this.modelList = this.sortValues([...new Set(data.map(item => item?.modelName).filter(name => name != null))], 'model');
    this.seriesList = this.sortValues([...new Set(data.map(item => item?.seriesName).filter(name => name != null))], 'series');
    this.engineList = this.sortValues([...new Set(data.map(item => item?.engineName).filter(name => name != null))], 'engine');
    this.yearList = this.sortValues([...new Set(data.map(item => item?.year).filter(year => year != null))], 'year');
  }

  resetSearch() {
    this.spinnerService.show('bmwSpinner');
    this.inputModel = '';
    this.inputSeries = '';
    this.inputEngine = '';
    this.inputYear = '';
    this.modelMapAll = [];
    this.errorMessage = '';
    
    this.filteredModelList = [];
    this.filteredSeriesList = [];
    this.filteredEngineList = [];
    this.filteredYearList = [];
    
    this.populateAllOptionLists(this.modelMapObjAll);
    this.hideAllDropdowns();
    this.spinnerService.hide('bmwSpinner');
  }

  viewAllModels() {
    this.resetSearch();
    this.modelMapAll = [...this.modelMapObjAll];
  }
  
  hideAllDropdowns() {
    setTimeout(() => {
      this.showModelDropdown = false;
      this.showSeriesDropdown = false;
      this.showEngineDropdown = false;
      this.showYearDropdown = false;
    }, 200);
  }

  filterModelMapSeries() {
    this.modelMapObjDisplay = this.modelMapObjDisplay.filter(e => (e.seriesName.includes(this.inputSeries)));
    this.modelMapObjDisplay.map(data => {
      this.modelList = this.smallFilter(data, "modelName");
      this.yearList = this.smallFilter(data, "year");
      this.engineList = this.smallFilter(data, "engineName");
    });
    // this.seriesFilter = this.inputSeries;
    this.filterMapList(this.modelMapObjDisplay);
    this.searchVehicles();
    this.analyticCallModelMapSeries();
  }
  analyticCallModelMapSeries() {
    if (this.inputSeries != '' || this.inputSeries != 'undefined') {
      this.analyticsService.trackModelMapSeriesValue(this.inputSeries);
    }
  }
  filterModelMapYear() {
    this.modelMapObjDisplay = this.modelMapObjDisplay.filter(e => (e.year.includes(this.inputYear)));
    this.modelMapObjDisplay.map(data => {
      this.modelList = this.smallFilter(data, "modelName");
      this.seriesList = this.smallFilter(data, "seriesName");
      this.engineList = this.smallFilter(data, "engineName");
    });
    // this.yearFilter = this.inputYear;
    this.filterMapList(this.modelMapObjDisplay);
    this.searchVehicles();
    this.analyticCallModelMapYear();
  }
  analyticCallModelMapYear() {
    if (this.inputYear != '' || this.inputYear != 'undefined') {
      this.analyticsService.trackModelMapYearValue(this.inputYear);
    }
  }
  filterModelMapEngine() {
    this.modelMapObjDisplay = this.modelMapObjDisplay.filter(e => (e.engineName.includes(this.inputEngine)));
    this.modelMapObjDisplay.map(data => {
      this.modelList = this.smallFilter(data, "modelName");
      this.seriesList = this.smallFilter(data, "seriesName");
      this.yearList = this.smallFilter(data, "year");
    });
    // this.yearFilter = this.inputYear;
    this.filterMapList(this.modelMapObjDisplay);
    this.searchVehicles();
    this.analyticCallModelMapEngine();
  }
  analyticCallModelMapEngine() {
    if (this.inputEngine != '' || this.inputEngine != 'undefined') {
      this.analyticsService.trackModelMapEngineValue(this.inputEngine);
    }
  }
  filterModelMapModel() {
    this.modelMapObjDisplay = this.modelMapObjDisplay.filter(e => (e.modelName.includes(this.inputModel)));
    this.modelMapObjDisplay.map(data => {
      this.engineList = this.smallFilter(data, "engineName");
      this.seriesList = this.smallFilter(data, "seriesName");
      this.yearList = this.smallFilter(data, "year");
    });
    // this.yearFilter = this.inputYear;
    this.filterMapList(this.modelMapObjDisplay);
    this.searchVehicles();
    this.analyticCallModelMapModel();
  }
  analyticCallModelMapModel() {
    if (this.inputModel != '' || this.inputModel != 'undefined') {
      this.analyticsService.trackModelMapModelValue(this.inputModel);
    }
  }
  smallFilter(data, element: string) {
    let tempModelList = [];
    tempModelList.push(data['element']);
    return tempModelList;
  }

  filterMapList(modelMap: ModelMap[]) {
    if (!this.modelMapObjDisplay || !Array.isArray(this.modelMapObjDisplay)) {
      return;
    }

    this.spinnerService.show('bmwSpinner');

    this.modelMapObjDisplay.forEach(element => {
      if (!element) return;

      if (element.modelName != null) {
        let tempModelList = [];
        tempModelList.push(element.modelName);
        this.modelList.push(...tempModelList);
      }

      if (element.seriesName != null) {
        let tempSeriesName = [];
        tempSeriesName.push(element.seriesName);
        this.seriesList.push(...tempSeriesName);
      }

      if (element.engineName != null) {
        let tempEngineName = [];
        tempEngineName.push(element.engineName);
        this.engineList.push(...tempEngineName);
      }

      if (element.year != null) {
        let tempYear = [];
        tempYear.push(element.year);
        this.yearList.push(...tempYear);
      }
    });

    this.modelList = this.sortValues(Array.from(new Set(this.modelList)), 'model');
    this.seriesList = this.sortValues(Array.from(new Set(this.seriesList)), 'series');
    this.engineList = this.sortValues(Array.from(new Set(this.engineList)), 'engine');
    this.yearList = this.sortValues(Array.from(new Set(this.yearList)), 'year');

    this.spinnerService.hide('bmwSpinner');
  }

  initializeFilter() {
    this.searchFilter = {
      'modelName': this.inputModel || '',
      'seriesName': this.inputSeries || '',
      'engineName': this.inputEngine || '',
      'year': this.inputYear || ''
    };
  }
  screenRotate(event) {
    this.screenWidth = this.utilityService.getScreenWidth();
  }

  searchVehicles() {
    this.spinnerService.show('bmwSpinner');
    this.modelMapAll = this.modelMapObjDisplay;
    this.spinnerService.hide('bmwSpinner');
  }

  removeFromFilter(filterKey) {
    switch (filterKey) {
      case 'modelName':
        this.inputModel = '';
        this.initializeFilter();
        break;
      case 'seriesName':
        this.inputSeries = '';
        this.initializeFilter();
        break;
      case 'engineName':
        this.inputEngine = '';
        this.initializeFilter();
        break;
      case 'year':
        this.inputYear = '';
        this.initializeFilter();
        break;
    }
  }
  printResults() {
    let printContents, popupWindow, footerMessage;
    printContents = this.resultsContentDiv.nativeElement.innerHTML;

    if (APP_SETTINGS.isBMW == 'true') {
      footerMessage = APP_SETTINGS.bmwCopyright;
    } else if (APP_SETTINGS.isMINI == 'true') {
      footerMessage = APP_SETTINGS.miniCopyright;
    } else if (APP_SETTINGS.isRollsRoyce == 'true') {
      footerMessage = APP_SETTINGS.rollsRoyceCopyright;
    } else {
      footerMessage = APP_SETTINGS.motorradCopyright;
    }

    popupWindow = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
    popupWindow.document.open();
    // Fix the css to import from file
    const htmlContent = `
    <html>
      <head>
        <title>Model_Map</title>
        <style>

        body {
          font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
        }
        .bmw-lg-bulletin-section-header{
          font-weight: bold; 
          font-size: 18px;
        }
        .print-title{
          font-weight: bold;
          font-size: 24px;
          text-align: center;
          margin-bottom: 10px;
        }
        .flex-container { 
          display: flex;
        }
        .row { flex-direction: row; }
        .column { flex-direction: column; }
        .wrap { flex-wrap: wrap; }
        
        /* Global Flex Utility.*/
        .flex { flex: 1 1 0%; }
        .flex-1  { flex: 1 1 1%; }
        .flex-2  { flex: 0 0 2%; }
        .flex-3  { flex: 0 0 3%; }
        .flex-4  { flex: 0 0 4%; }
        .flex-5  { flex: 0 0 5%; }
        .flex-6  { flex: 0 0 6%; }
        .flex-7  { flex: 0 0 7%; }
        .flex-8  { flex: 0 0 8%; }
        .flex-9  { flex: 0 0 9%; }
        .flex-10 { flex: 0 0 10%; }
        .flex-11 { flex: 0 0 11%; }
        .flex-12 { flex: 0 0 12%; }
        .flex-12-5 { flex: 0 0 12.5%; }
        .flex-13 { flex: 0 0 13%; }
        .flex-14 { flex: 0 0 14%; }
        .flex-15 { flex: 0 0 15%; }
        .flex-15-5 { flex: 0 0 15.5%; }
        .flex-16 { flex: 0 0 15%; }
        .flex-17 { flex: 0 0 17%; }
        .flex-18 { flex: 0 0 18%; }
        .flex-19 { flex: 0 0 19%; }
        .flex-20 { flex: 0 0 20%; }
        .flex-21 { flex: 0 0 21%; }
        .flex-22 { flex: 0 0 22%; }
        .flex-23 { flex: 0 0 23%; }
        .flex-24 { flex: 0 0 24%; }
        .flex-25 { flex: 0 0 25%; }
        .flex-26 { flex: 0 0 26%; }
        .flex-27 { flex: 0 0 27%; }
        .flex-28 { flex: 0 0 28%; }
        .flex-29 { flex: 0 0 29%; }
        .flex-30 { flex: 0 0 30%; }
        .flex-31 { flex: 0 0 31%; }
        .flex-32 { flex: 0 0 32%; }
        .flex-33 { flex: 0 0 33%; }
        .flex-34 { flex: 0 0 34%; }
        .flex-35 { flex: 0 0 35%; }
        .flex-36 { flex: 0 0 36%; }
        .flex-37 { flex: 0 0 37%; }
        .flex-38 { flex: 0 0 38%; }
        .flex-39 { flex: 0 0 39%; }
        .flex-40 { flex: 0 0 40%; }
        .flex-41 { flex: 0 0 41%; }
        .flex-42 { flex: 0 0 42%; }
        .flex-43 { flex: 0 0 43%; }
        .flex-44 { flex: 0 0 44%; }
        .flex-45 { flex: 0 0 45%; }
        .flex-46 { flex: 0 0 46%; }
        .flex-47 { flex: 0 0 47%; }
        .flex-48 { flex: 0 0 48%; }
        .flex-49 { flex: 0 0 49%; }
        .flex-50 { flex: 0 0 50%; }
        .flex-51 { flex: 0 0 51%; }
        .flex-52 { flex: 0 0 52%; }
        .flex-53 { flex: 0 0 53%; }
        .flex-54 { flex: 0 0 54%; }
        .flex-55 { flex: 0 0 55%; }
        .flex-56 { flex: 0 0 56%; }
        .flex-57 { flex: 0 0 57%; }
        .flex-58 { flex: 0 0 58%; }
        .flex-59 { flex: 0 0 59%; }
        .flex-60 { flex: 0 0 60%; }
        .flex-61 { flex: 0 0 61%; }
        .flex-62 { flex: 0 0 62%; }
        .flex-63 { flex: 0 0 63%; }
        .flex-64 { flex: 0 0 64%; }
        .flex-64-5 { flex: 0 0 64.5%; }
        .flex-65 { flex: 0 0 65%; }
        .flex-66 { flex: 0 0 66%; }
        .flex-67 { flex: 0 0 67%; }
        .flex-68 { flex: 0 0 68%; }
        .flex-69 { flex: 0 0 69%; }
        .flex-70 { flex: 0 0 70%; }
        .flex-71 { flex: 0 0 71%; }
        .flex-72 { flex: 0 0 72%; }
        .flex-73 { flex: 0 0 73%; }
        .flex-74 { flex: 0 0 74%; }
        .flex-75 { flex: 0 0 75%; }
        .flex-76 { flex: 0 0 76%; }
        .flex-77 { flex: 0 0 77%; }
        .flex-78 { flex: 0 0 78%; }
        .flex-79 { flex: 0 0 79%; }
        .flex-80 { flex: 0 0 80%; }
        .flex-81 { flex: 0 0 81%; }
        .flex-81-3 { flex: 0 0 81.3%; }
        .flex-82 { flex: 0 0 82%; }
        .flex-83 { flex: 0 0 83%; }
        .flex-83-3 { flex: 0 0 83.3%; }
        .flex-84 { flex: 0 0 84%; }
        .flex-85 { flex: 0 0 85%; }
        .flex-86 { flex: 0 0 86%; }
        .flex-87 { flex: 0 0 87%; }
        .flex-88 { flex: 0 0 88%; }
        .flex-89 { flex: 0 0 89%; }
        .flex-90 { flex: 0 0 90%; }
        .flex-91 { flex: 0 0 91%; }
        .flex-92 { flex: 0 0 92%; }
        .flex-93 { flex: 0 0 93%; }
        .flex-94 { flex: 0 0 94%; }
        .flex-95 { flex: 0 0 95%; }
        .flex-96 { flex: 0 0 96%; }
        .flex-97 { flex: 0 0 97%; }
        .flex-98 { flex: 0 0 98%; }
        .flex-99 { flex: 0 0 99%; }
        .flex-100 { flex: 0 0 100%; }
        .flex-120 { flex: 0 0 120%; }

        /* Pixel-based Flex Classes */
        .flex-30-px { flex: 0 0 30px; }
        .flex-65-px { flex: 0 0 65px; }
        .flex-90-px { flex: 0 0 90px; }
        .flex-95-px { flex: 0 0 95px; }
        .flex-125-px { flex: 0 0 125px; }
        .flex-150-px { flex: 0 0 150px; }
        .flex-170-px { flex: 0 0 170px; }
        .flex-187-px { flex: 0 0 187px; }
        .flex-190-px { flex: 0 0 190px; }
        .flex-200-px { flex: 0 0 200; }
        .flex-202-px { flex: 0 0 202; }
        .flex-214-px { flex: 0 0 214; }
        .flex-250-px { flex: 0 0 250px; }
        .flex-275-px { flex: 0 0 275px; }
        .flex-276-px { flex: 0 0 276px; }
        .flex-300-px { flex: 0 0 300px; }
        .flex-444-px { flex: 0 0 444px; }
        .flex-500-px { flex: 0 0 500px; }
        .flex-614-px { flex: 0 0 614px; }
        .flex-865-px { flex: 0 0 865px; }

        /* Flex Align Utility */
        .flex-align-center {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-grow: 1;
        }

        .justify-center-align-center {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .space-between {
          justify-content: space-between;
        }

        .center {
          align-items: center;
        }

        .space-around-center {
          display: flex;
          justify-content: space-around;
          align-items: center;
        }

        .flex-align-center-flex-end {
          display: flex;
          justify-content: flex-end;
          align-items: center;
        }

        .align-center-justify-evenly {
          display: flex;
          justify-content: flex-end;
          align-items: center;
        }

        .flex-column-align-end {
          display: flex;
          flex-direction: column;
          align-items: end;
        }
        .grow {
          flex-grow: 1;
        }
        </style>
      </head>
      <body onload="window.print();window.close()">
      <div style=" position: fixed;bottom: 0;
      width: 100%; 
      text-align: center;">
        ${footerMessage}
      </div>

      <table style="width:100%">
      <tbody>
      <tr>
      <td >
      <div style="page-break-after: always;">
        ${printContents} <br />
        </div>
      </td>
      </tr>
      </tbody>
  <tfoot>
    <tr>
      <td>
      <div style="height: 20px;"></div>
      
    </td>
    </tr>
  </tfoot>
  </table>
  
      </body>
    </html>`;
    popupWindow.document.write(htmlContent);
    popupWindow.document.close();
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

}
